import React, { useEffect, useState, useMemo } from 'react';
import { useGetViewsQuery, useGetKeypointCollectionClassesQuery } from '../../redux/slices/views';
import {
  KeypointFrame,
  KeypointFrameWithKeypointLabels,
  KeypointCollectionLabel,
} from 'models/keypoints';
import { Col, Empty, Pagination, Row, Typography, Button, message, Card } from 'antd';
import {
  KeypointsWrapper,
  StatsBarWrapper,
  StatsContentWrapper,
  AcceptAllButtonWrapper,
  PaginationWrapper,
} from './styled';
import KeypointItem from './components/KeypointItem';
import KeypointFilter from 'components/Filters/KeypointFilter';
import Skeleton from '../Skeleton/Skeleton';
import { useCreateReviewMutation, useGetKeypointStatsQuery } from '../../redux/slices/reviews';
import { AdvancedFiltersOptions, AdvancedFilterValue } from 'models/advancedFilters';

import { useKeypointFilterState } from '../../hooks/useFilterState';
import { useSearchParams } from 'react-router-dom';
import RejectModal from './components/RejectModal';
import AcceptModal from './components/AcceptModal';
import { Decision } from '../../models/reviews';
import { View } from 'models/views';
import ReviewModal from './components/ReviewModal';
import { useGetKeypointFramesQuery } from '../../redux/slices/keypoints';

const Keypoints = () => {
  const { filterState } = useKeypointFilterState();

  const hasAnyFilter = (filterState: any) => {
    if (filterState.view) return true;
    if (filterState.features && filterState.features.length > 0) return true;
    if (filterState.advancedFilters) {
      return Object.values(filterState.advancedFilters).some(
        (v: any) =>
          (Array.isArray(v) && v.length > 0) ||
          (typeof v === 'object' && v !== null && Object.keys(v).length > 0 && 'name' in v) ||
          (typeof v === 'string' && v.trim() !== '')
      );
    }
    return false;
  };

  const hasActiveFilters = useMemo(() => hasAnyFilter(filterState), [filterState]);
  const { data: views } = useGetViewsQuery('');
  const {
    data: kpClasses,
    isLoading: isLoadingKpClasses,
    isError: isKpClassesError,
  } = useGetKeypointCollectionClassesQuery();
  const [page, setPage] = useState(1);
  const [size, setSize] = useState(6);
  const [searchParams, setSearchParams] = useSearchParams();
  const [acceptIds] = useState<string[]>([]);
  const [isAcceptAllLoading, setIsAcceptAllLoading] = useState(false);
  const [isAcceptAllDisabled] = useState(false);
  const [viewOptions, setViewOptions] = useState<AdvancedFilterValue[]>([]);
  const [featureOptions, setFeatureOptions] = useState<AdvancedFilterValue[]>([]);
  const [_viewMap, setViewMap] = useState<Record<string, View>>({});
  const [advancedFiltersOptions] = useState<AdvancedFiltersOptions>({
    datasources: [
      { uuid: 'ABBOTT', name: 'ABBOTT' },
      { uuid: 'ARKHEART', name: 'ARKHEART' },
      { uuid: 'CONWAY', name: 'CONWAY' },
      { uuid: 'DP', name: 'DP' },
      { uuid: 'FSV', name: 'FSV' },
    ],
    dicomICID: '',
    study: '',
    keypointICID: '',
    technician: [],
    created_at_after: undefined,
    created_at_before: undefined,
    updated_at_after: undefined,
    updated_at_before: undefined,
  });
  const [visibleKeypoints, setVisibleKeypoints] = useState<Map<string, Set<string>>>(new Map());
  const [rejectModalOpen, setRejectModalOpen] = useState(false);
  const [_, setRejectFrameKey] = useState<string | null>(null);
  const [rejectFrameKeypoints, setRejectFrameKeypoints] = useState<KeypointCollectionLabel[]>([]);
  const [createReview] = useCreateReviewMutation();
  const [shouldFetchFrames, setShouldFetchFrames] = useState(false);
  const [hasAutoAppliedFromQueryParams, setHasAutoAppliedFromQueryParams] = useState(false);

  // ✅ Missing state added (fixes TS errors)
  const [isReviewModalOpen, setReviewModalOpen] = useState(false);

  const { data: keypointStats, refetch: refetchKeypointStats } = useGetKeypointStatsQuery();

  // Local decisions map (keyed by ICID)
  const [localDecisions, setLocalDecisions] = useState<Record<string, Decision>>({});
  const getLocalDecision = (id: string) => localDecisions[id];
  const setDecisionsFor = (ids: string[], decision: Decision) => {
    setLocalDecisions(prev => {
      const next = { ...prev };
      ids.forEach(id => {
        next[id] = decision;
      });
      return next;
    });
  };
  const onLocalDecision = (id: string, decision: Decision) => setDecisionsFor([id], decision);
  const onLocalDecisionRemove = (id: string) =>
    setLocalDecisions(prev => {
      const next = { ...prev };
      delete next[id];
      return next;
    });
  const [removedReviews, setRemovedReviews] = useState<Record<string, boolean>>({});
  const handleMarkRemoved = (ids: string[]) => {
    setRemovedReviews(prev => {
      const next = { ...prev };
      ids.forEach(id => (next[id] = true));
      return next;
    });
    setLocalDecisions(prev => {
      const next = { ...prev };
      ids.forEach(id => delete next[id]);
      return next;
    });
  };

  // State to control when to fetch frames
  const [isPaginationLoading, setIsPaginationLoading] = useState(false);

  // Use the new getKeypointFrames query - only when shouldFetchFrames is true
  const queryParams = useMemo(
    () => ({
      page,
      size,
      view: filterState.view,
      features: filterState.features,
      advancedFilters: {
        datasources: filterState.advancedFilters.datasources,
        dicomICID: filterState.advancedFilters.dicomICID,
        study: filterState.advancedFilters.study,
        annotationSetName: filterState.advancedFilters.annotationSetName,
        keypointICID: filterState.advancedFilters.keypointICID,
        frameId: (filterState.advancedFilters as any).frameId,
        reviewStatus: (filterState.advancedFilters as any).reviewStatus,
        technician: filterState.advancedFilters.technician,
        created_at_after: filterState.advancedFilters.created_at_after,
        created_at_before: filterState.advancedFilters.created_at_before,
        updated_at_after: filterState.advancedFilters.updated_at_after,
        updated_at_before: filterState.advancedFilters.updated_at_before,
      },
    }),
    [page, size, filterState]
  );

  const {
    data: framesPage,
    isLoading: isLoadingGetFrames,
    isFetching,
  } = useGetKeypointFramesQuery(queryParams, {
    skip: !shouldFetchFrames, // Skip the query until filters are applied
  });

  // Sync initial page from URL (?page=...)
  useEffect(() => {
    const qp = searchParams.get('page');
    const next = qp ? parseInt(qp, 10) : 1;
    if (Number.isFinite(next) && next > 0 && next !== page) {
      setPage(next);
    }
  }, [searchParams]);

  // Auto-enable fetch when page is present
  useEffect(() => {
    if (searchParams.get('page') && !shouldFetchFrames) {
      setShouldFetchFrames(true);
    }
  }, [searchParams, shouldFetchFrames]);

  const writePageToURL = (next: number, nextSize?: number) => {
    const params = new URLSearchParams(window.location.search);
    params.set('page', String(next));
    const sizeToWrite = typeof nextSize === 'number' ? nextSize : size;
    params.set('items_per_page', String(sizeToWrite));
    setSearchParams(params, { replace: true });
  };

  // Sync initial size from URL (?items_per_page=...); default to current `size` if invalid/missing
  useEffect(() => {
    const qp = searchParams.get('items_per_page');
    const next = qp ? parseInt(qp, 10) : undefined;
    if (next && Number.isFinite(next) && next > 0 && next !== size) {
      setSize(next);
    }
  }, [searchParams]);

  // Reset pagination loading when the current fetch cycle completes
  useEffect(() => {
    if (!isFetching && isPaginationLoading) {
      setIsPaginationLoading(false);
    }
  }, [isFetching, isPaginationLoading]);

  // Fallback timeout to reset pagination loading if API call takes too long
  useEffect(() => {
    if (isPaginationLoading) {
      const timeout = setTimeout(() => {
        setIsPaginationLoading(false);
      }, 10000); // 10 second timeout

      return () => clearTimeout(timeout);
    }
  }, [isPaginationLoading]);

  // Map backend response: use keypointCollectionLabels for each frame
  const framesWithKeypointLabels: KeypointFrameWithKeypointLabels[] = useMemo(
    () =>
      framesPage?.results.map((frame: KeypointFrame) => ({
        frame,
        keypoints: frame.keypointCollectionLabels || [],
      })) || [],
    [framesPage]
  );

  const keypointPage = {
    items: framesPage?.results || [],
    pages: Math.ceil((framesPage?.count || 0) / size),
    size: size,
    total: framesPage?.count || 0,
  };

  useEffect(() => {
    if (framesWithKeypointLabels.length > 0) {
      const initialVisibility = new Map<string, Set<string>>();
      framesWithKeypointLabels.forEach(frameGroup => {
        const frameKey = frameGroup.frame.icid;
        const keypointIds = new Set(frameGroup.keypoints.map(kp => kp.icid));
        initialVisibility.set(frameKey, keypointIds);
      });
      setVisibleKeypoints(initialVisibility);
    }
  }, [framesWithKeypointLabels]);

  const hasAnyQueryParam = useMemo(
    () =>
      [
        'view',
        'features',
        'datasources',
        'dicomICID',
        'study',
        'keypointICID',
        'technician',
        'created_at_after',
        'created_at_before',
        'updated_at_after',
        'updated_at_before',
        'frame_id',
        'review_status',
      ].some(param => searchParams.get(param)),
    [searchParams]
  );

  const isFilterStateSyncedWithQuery = (fs: any) => {
    const viewParam = searchParams.get('view');
    if (viewParam) {
      const matchesView = fs.view && (fs.view.name === viewParam || fs.view.uuid === viewParam);
      if (!matchesView) return false;
    }

    const featuresParam = searchParams.get('features');
    if (featuresParam) {
      const requested = featuresParam
        .split(',')
        .map(f => decodeURIComponent(f))
        .map(f => f.replace(/\+/g, ' '));
      const current = (fs.features || []).map((f: any) => [f.name, f.uuid]).flat();
      const allIncluded = requested.every((r: any) => current.includes(r));
      if (!allIncluded) return false;
    }

    const singleStringParams = [
      'dicomICID',
      'study',
      'keypointICID',
      'technician',
      'created_at_after',
      'created_at_before',
      'updated_at_after',
      'updated_at_before',
    ] as const;

    for (const key of singleStringParams) {
      const qp = searchParams.get(key);
      if (qp) {
        if ((fs.advancedFilters as any)[key] !== qp) return false;
      }
    }

    const datasourceParam = searchParams.get('datasources');
    if (datasourceParam) {
      const ds = fs.advancedFilters?.datasources;
      if (!ds || (ds.name !== datasourceParam && ds.uuid !== datasourceParam)) return false;
    }
    // --- Frame ID param ---
    const frameIdParam = searchParams.get('frame_id');
    if (frameIdParam) {
      const matchesFrameId =
        (fs.advancedFilters as any).frameId === decodeURIComponent(frameIdParam);
      if (!matchesFrameId) return false;
    }
    // --- Review status param (comma-separated) ---
    const reviewStatusParam = searchParams.get('review_status');
    if (reviewStatusParam) {
      const requested = decodeURIComponent(reviewStatusParam)
        .split(',')
        .map(s => s.trim())
        .filter(Boolean);
      const current = Array.isArray((fs.advancedFilters as any).reviewStatus)
        ? (fs.advancedFilters as any).reviewStatus.map((s: string) => String(s))
        : [];
      const allIncluded = requested.every((r: string) => current.includes(r));
      if (!allIncluded) return false;
    }

    return true;
  };

  useEffect(() => {
    let viewList: View[] = [];
    if (
      views &&
      typeof views === 'object' &&
      'results' in views &&
      Array.isArray((views as any).results)
    ) {
      viewList = (views as { results: View[] }).results;
    } else if (Array.isArray(views)) {
      viewList = views as View[];
    }
    if (viewList.length > 0) {
      // Sort views alphabetically by name
      const sortedViews = [...viewList].sort((a, b) => a.view.localeCompare(b.view));
      const formattedView = sortedViews.map((view: View) => ({ uuid: view.uuid, name: view.view }));
      setViewOptions(formattedView);
      // Build a map for quick lookup
      const map: Record<string, View> = {};
      sortedViews.forEach((view: View) => {
        map[view.uuid] = view;
      });
      setViewMap(map);
    } else {
      setViewOptions([]);
      setViewMap({});
    }
  }, [views]);

  // Populate keypoint feature options from the keypoint-collection-classes endpoint on load
  useEffect(() => {
    if (!kpClasses) {
      setFeatureOptions([]);
      return;
    }
    let items: any[] = [];
    if (Array.isArray(kpClasses)) {
      items = kpClasses as any[];
    } else if (
      typeof kpClasses === 'object' &&
      'results' in (kpClasses as any) &&
      Array.isArray((kpClasses as any).results)
    ) {
      items = (kpClasses as any).results;
    }
    const sorted = [...items].sort((a, b) => String(a.value).localeCompare(String(b.value)));
    const features: AdvancedFilterValue[] = sorted.map((item: any) => ({
      uuid: item.uuid,
      name: item.value,
    }));
    setFeatureOptions(features);
  }, [kpClasses]);

  // No view-driven feature population; features are global keypoint collection classes

  const onHandleClear = () => {
    setShouldFetchFrames(false);
    setPage(1);
    setIsPaginationLoading(false);
    writePageToURL(1);
    // Do not clear featureOptions; they remain populated from endpoint
  };

  const clearKeypoints = () => {
    onHandleClear();
  };

  const onHandleApply = () => {
    setShouldFetchFrames(true);
    setIsPaginationLoading(false);
    setPage(1);
    writePageToURL(1);
  };

  const onPaginationChange = (_page: number, pageSize: number) => {
    if (!shouldFetchFrames) return;
    setIsPaginationLoading(true);
    window.scrollTo({ top: 0, behavior: 'auto' });
    setPage(_page);
    setSize(pageSize);
    writePageToURL(_page, pageSize);
  };

  const onShowSizeChange = (_current: number, newSize: number) => {
    if (!shouldFetchFrames) return;
    setIsPaginationLoading(true);
    window.scrollTo({ top: 0, behavior: 'auto' });
    setSize(newSize);
    setPage(1);
    writePageToURL(1, newSize);
  };

  // Helper: has any decision (local or server flags)
  const hasDecision = (kp: KeypointCollectionLabel) => {
    const local = getLocalDecision(kp.icid);
    return local !== undefined || kp.isApproved || kp.isRejected;
  };

  const allAcceptedOrRejected = framesWithKeypointLabels.every(frameGroup =>
    frameGroup.keypoints.every(kp => hasDecision(kp))
  );

  const handleAcceptAll = () => {
    setReviewModalOpen(true);
  };

  const handleConfirmAcceptAll = async () => {
    setIsAcceptAllLoading(true);
    const keypointsToAccept = framesWithKeypointLabels
      .flatMap(frameGroup => frameGroup.keypoints)
      .filter(kp => !hasDecision(kp));

    if (keypointsToAccept.length === 0) {
      message.info('All keypoints on this page have already been reviewed.');
      setIsAcceptAllLoading(false);
      setReviewModalOpen(false);
      return;
    }

    const reviewsToCreate = keypointsToAccept.map(kp => ({
      object_uuid: kp.uuid,
      object_icid: kp.icid,
      object_type: 'KEYPOINT_COLLECTION_LABEL',
      object_view: 'default',
      object_view_id: 0,
      decision: Decision.ACCEPTED,
      comment: '',
      annotation_link: '',
    }));

    try {
      await createReview(reviewsToCreate).unwrap();
      message.success('All remaining keypoints on this page have been accepted.');
      setDecisionsFor(
        keypointsToAccept.map(k => k.icid),
        Decision.ACCEPTED
      );
    } catch (error) {
      message.error('Failed to accept all keypoints.');
    } finally {
      setIsAcceptAllLoading(false);
      setReviewModalOpen(false);
    }
  };

  const onSelectView = (_value: AdvancedFilterValue) => {
    // No-op for feature options; view still affects frames query
  };

  const handleKeypointToggle = (frameKey: string, keypointId: string, visible: boolean) => {
    setVisibleKeypoints(prevMap => {
      const newMap = new Map(prevMap);
      const frameVisibleSet = new Set(newMap.get(frameKey) || []);

      if (visible) {
        frameVisibleSet.add(keypointId);
      } else {
        frameVisibleSet.delete(keypointId);
      }

      newMap.set(frameKey, frameVisibleSet);
      return newMap;
    });
  };

  const handleFrameAccept = async (keypoints: KeypointCollectionLabel[]) => {
    // Open accept modal to choose which keypoints to accept
    setAcceptFrameKeypoints(keypoints);
    setAcceptModalOpen(true);
  };

  const handleFrameReject = (frameKey: string, keypoints: KeypointCollectionLabel[]) => {
    setRejectFrameKey(frameKey);
    setRejectFrameKeypoints(keypoints);
    setRejectModalOpen(true);
  };

  const handleRejectModalOk = async (rejectedIds: string[], comment: string) => {
    if (!rejectFrameKeypoints) return;
    try {
      // Only submit reviews for explicitly rejected keypoints.
      const toReject = rejectFrameKeypoints.filter(kp => rejectedIds.includes(kp.icid));
      if (toReject.length === 0) {
        message.info('No keypoints selected for rejection.');
        setRejectModalOpen(false);
        setRejectFrameKey(null);
        setRejectFrameKeypoints([]);
        return;
      }

      const reviewsToCreate = toReject.map(kp => ({
        object_uuid: kp.uuid,
        object_icid: kp.icid,
        object_type: 'KEYPOINT_COLLECTION_LABEL',
        object_view: 'default',
        object_view_id: 0,
        decision: Decision.REJECTED,
        comment,
        annotation_link: '',
      }));
      await createReview(reviewsToCreate).unwrap();
      message.success('Rejected keypoints submitted successfully');
      const rejectedIcids: string[] = toReject.map(k => k.icid);
      if (rejectedIcids.length) setDecisionsFor(rejectedIcids, Decision.REJECTED);
    } catch (error) {
      message.error('Failed to submit review');
    }
    setRejectModalOpen(false);
    setRejectFrameKey(null);
    setRejectFrameKeypoints([]);
  };

  const handleRejectModalCancel = () => {
    setRejectModalOpen(false);
    setRejectFrameKey(null);
    setRejectFrameKeypoints([]);
  };

  // Accept modal for keypoints
  const [acceptModalOpen, setAcceptModalOpen] = useState(false);
  const [acceptFrameKeypoints, setAcceptFrameKeypoints] = useState<KeypointCollectionLabel[]>([]);

  const handleAcceptModalOk = async (acceptedIds: string[], comment: string) => {
    if (!acceptFrameKeypoints) return;
    try {
      const toAccept = acceptFrameKeypoints.filter(kp => acceptedIds.includes(kp.icid));
      if (toAccept.length === 0) {
        message.info('No keypoints selected for acceptance.');
        setAcceptModalOpen(false);
        setAcceptFrameKeypoints([]);
        return;
      }
      const reviewsToCreate = toAccept.map(kp => ({
        object_uuid: kp.uuid,
        object_icid: kp.icid,
        object_type: 'KEYPOINT_COLLECTION_LABEL',
        object_view: 'default',
        object_view_id: 0,
        decision: Decision.ACCEPTED,
        comment,
        annotation_link: '',
      }));
      await createReview(reviewsToCreate).unwrap();
      message.success('Selected keypoints accepted successfully');
      setDecisionsFor(
        toAccept.map(k => k.icid),
        Decision.ACCEPTED
      );
    } catch (error) {
      message.error('Failed to submit review');
    }
    setAcceptModalOpen(false);
    setAcceptFrameKeypoints([]);
  };

  const _refreshReviews = () => undefined;

  useEffect(() => {
    if (!hasAutoAppliedFromQueryParams) {
      // hasAnyQueryParam computed via useMemo
      const hydrated = hasAnyFilter(filterState);
      const synced = isFilterStateSyncedWithQuery(filterState);
      if (hasAnyQueryParam && hydrated && synced) {
        setShouldFetchFrames(true);
        setHasAutoAppliedFromQueryParams(true);
      }
    }
  }, [searchParams, filterState, hasAutoAppliedFromQueryParams]);

  useEffect(() => {
    // Refetch global keypoint statistics only when no active filters are present
    if (!hasActiveFilters) {
      refetchKeypointStats();
    }
  }, [page, hasActiveFilters, refetchKeypointStats]);

  // ------- Normalize stats for UI (frames stats after Apply; global stats otherwise) -------
  const selectedStats = useMemo(() => {
    if (shouldFetchFrames && framesPage?.stats) {
      const s: any = framesPage.stats;
      return {
        total: s.total_found ?? s.totalFound ?? 0,
        accepted: s.accepted ?? 0,
        rejected: s.rejected ?? 0,
        toBeReviewed: s.to_be_reviewed ?? s.toBeReviewed ?? 0,
      };
    }
    if (keypointStats) {
      const s: any = keypointStats;
      return {
        total: s.totalFound ?? s.total_found ?? 0,
        accepted: s.accepted ?? 0,
        rejected: s.rejected ?? 0,
        toBeReviewed: s.toBeReviewed ?? s.to_be_reviewed ?? 0,
      };
    }
    return null;
  }, [shouldFetchFrames, framesPage, keypointStats]);

  return (
    <KeypointsWrapper>
      <KeypointFilter
        secondaryOptions={featureOptions}
        primaryOptions={viewOptions}
        advancedFiltersOptions={advancedFiltersOptions}
        handlePrimaryOptChange={onSelectView}
        onHandleClear={onHandleClear}
        onHandleApply={onHandleApply}
        isLoadingSegmentationClasses={isLoadingKpClasses}
        isSegmentationClassesError={isKpClassesError}
        clearSegmentations={clearKeypoints}
      />
      {/* Statistics Bar */}
      {selectedStats && (
        <StatsBarWrapper>
          <StatsContentWrapper>
            <Typography.Text style={{ fontSize: '16px', fontWeight: 'bold' }}>
              {selectedStats.total} Total
            </Typography.Text>
            <Typography.Text type="success" style={{ fontSize: '16px', fontWeight: 'bold' }}>
              {selectedStats.accepted} Accepted
            </Typography.Text>
            <Typography.Text type="danger" style={{ fontSize: '16px', fontWeight: 'bold' }}>
              {selectedStats.rejected} Rejected
            </Typography.Text>
            <Typography.Text type="warning" style={{ fontSize: '16px', fontWeight: 'bold' }}>
              {selectedStats.toBeReviewed} To-be-reviewed
            </Typography.Text>
          </StatsContentWrapper>
          {shouldFetchFrames && framesWithKeypointLabels && framesWithKeypointLabels.length > 0 && (
            <AcceptAllButtonWrapper>
              <Button
                type="primary"
                onClick={handleAcceptAll}
                disabled={allAcceptedOrRejected || isAcceptAllLoading || isAcceptAllDisabled}
                loading={isAcceptAllLoading}
                style={{
                  height: '40px',
                  fontWeight: 'bold',
                  fontSize: '16px',
                }}
              >
                Accept All On Page
              </Button>
            </AcceptAllButtonWrapper>
          )}
        </StatsBarWrapper>
      )}
      {!shouldFetchFrames ? (
        hasAnyQueryParam ? (
          <Skeleton />
        ) : (
          <Empty
            description={
              <Typography.Text type="secondary">
                Please apply filters to view keypoints.
              </Typography.Text>
            }
          />
        )
      ) : isLoadingGetFrames || isFetching || isPaginationLoading ? (
        <Skeleton />
      ) : framesWithKeypointLabels && framesWithKeypointLabels.length > 0 ? (
        <Row gutter={[24, 24]}>
          {framesWithKeypointLabels.map((frameGroup: KeypointFrameWithKeypointLabels) => {
            const frameKey = frameGroup.frame.icid;
            const frameVisibleKeypoints = visibleKeypoints.get(frameKey) || new Set();
            const allInFrameAccepted = frameGroup.keypoints.every(kp => {
              const local = getLocalDecision(kp.uuid);
              const wasRemoved = removedReviews ? !!removedReviews[kp.icid] : false;
              const serverAccepted = !wasRemoved && kp.isApproved;
              return local === Decision.ACCEPTED || serverAccepted;
            });

            return (
              <Col key={frameKey} xs={24} sm={24} md={12} lg={8} xl={8}>
                <Card
                  title={
                    <div>
                      <strong>
                        Frame #{frameGroup.frame.index} (ICID: {frameKey})<br />
                      </strong>
                      <div style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>
                        {frameGroup.keypoints.length} keypoint
                        {frameGroup.keypoints.length !== 1 ? 's' : ''}
                      </div>
                    </div>
                  }
                  style={{ minHeight: 400 }}
                >
                  {frameGroup.keypoints.length === 0 ? (
                    <Typography.Text type="secondary">No keypoints for this frame.</Typography.Text>
                  ) : (
                    <KeypointItem
                      key={frameGroup.keypoints[0].icid}
                      keypoint={frameGroup.keypoints[0]}
                      frame={frameGroup.frame}
                      frameFile={frameGroup.frame.fileUrl}
                      review={undefined}
                      acceptMe={acceptIds.includes(frameGroup.keypoints[0].icid)}
                      allKeypoints={frameGroup.keypoints}
                      onKeypointToggle={(keypointId: string, visible: boolean) =>
                        handleKeypointToggle(frameKey, keypointId, visible)
                      }
                      visibleKeypoints={frameVisibleKeypoints}
                      onFrameAccept={() => {
                        // open accept modal per-frame
                        setAcceptFrameKeypoints(frameGroup.keypoints);
                        setAcceptModalOpen(true);
                      }}
                      onFrameReject={() => handleFrameReject(frameKey, frameGroup.keypoints)}
                      frameReviewed={allInFrameAccepted}
                      onReviewChange={() => undefined}
                      getReviewFromKeypoint={() => undefined}
                      onLocalDecision={onLocalDecision}
                      onLocalDecisionRemove={onLocalDecisionRemove}
                      getLocalDecision={getLocalDecision}
                      removedReviews={removedReviews}
                      onMarkRemoved={handleMarkRemoved}
                      isRejected={frameGroup.keypoints.some(kp => {
                        const local = getLocalDecision(kp.icid);
                        return local === Decision.REJECTED || kp.isRejected;
                      })}
                    />
                  )}
                </Card>
              </Col>
            );
          })}
        </Row>
      ) : (
        <Empty
          description={
            <Typography.Text type="secondary">
              No keypoints found for the selected filters.
            </Typography.Text>
          }
        />
      )}
      {shouldFetchFrames && framesWithKeypointLabels && framesWithKeypointLabels.length > 0 && (
        <PaginationWrapper>
          <Pagination
            total={keypointPage.total}
            defaultPageSize={size}
            current={page}
            onShowSizeChange={onShowSizeChange}
            onChange={onPaginationChange}
            showTotal={total => `Total ${total} items`}
          />
        </PaginationWrapper>
      )}
      <RejectModal
        isModalOpen={rejectModalOpen}
        handleCancel={handleRejectModalCancel}
        handleOk={handleRejectModalOk}
        segmentations={rejectFrameKeypoints}
        initialRejectedIds={rejectFrameKeypoints
          .filter(kp => {
            const local = getLocalDecision(kp.icid);
            return local === Decision.REJECTED || kp.isRejected;
          })
          .map(kp => kp.icid)}
      />
      <AcceptModal
        isModalOpen={acceptModalOpen}
        handleCancel={() => {
          setAcceptModalOpen(false);
          setAcceptFrameKeypoints([]);
        }}
        handleOk={handleAcceptModalOk}
        segmentations={acceptFrameKeypoints as any}
        initialAcceptedIds={acceptFrameKeypoints
          .filter(kp => {
            const d = getLocalDecision(kp.icid);
            return d === Decision.ACCEPTED || kp.isApproved;
          })
          .map(kp => kp.icid)}
      />
      <ReviewModal
        isModalOpen={isReviewModalOpen}
        handleOk={handleConfirmAcceptAll}
        handleCancel={() => setReviewModalOpen(false)}
        loading={isAcceptAllLoading}
      />
    </KeypointsWrapper>
  );
};

export default Keypoints;
